import { tenantSlugFromPathOrHost } from '../utils/tenantDomains';

export const LEGACY_SESSION_TOKEN_KEY = 'session_token';
const COOKIE_PREFIX = 'tl_';
const COOKIE_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

const normalizeKeyPart = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/^www\./, '')
  .replace(/\/.*$/, '')
  .replace(/\.$/, '')
  .replace(/[^a-z0-9.-]+/g, '-');

export const currentPortalFromPath = (pathname = (typeof window !== 'undefined' ? window.location.pathname : '')) => {
  const path = String(pathname || '');
  if (/(^|\/)admin(\/|$)/.test(path)) return 'admin';
  if (/(^|\/)survey(\/|$)/.test(path)) return 'admin';
  if (/(^|\/)finance(\/|$)/.test(path)) return 'finance';
  if (/(^|\/)(?:agent|agent-portal|staff-portal)(\/|$)/.test(path)) return 'agent';
  return 'player';
};

export const sessionScopeKey = (tenant = tenantSlugFromPathOrHost(), portal = currentPortalFromPath()) => {
  const tenantKey = normalizeKeyPart(tenant) || 'main';
  const portalKey = normalizeKeyPart(portal) || 'player';
  return `session_token:${tenantKey}:${portalKey}`;
};

const storagePair = () => {
  if (typeof window === 'undefined') return [];
  return [localStorage, sessionStorage];
};

const sessionCookieName = (key) => `${COOKIE_PREFIX}${encodeURIComponent(key)}`;

const cookieSecureFlag = () => (
  typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : ''
);

const parseCookies = () => {
  if (typeof document === 'undefined') return [];
  return String(document.cookie || '')
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const idx = part.indexOf('=');
      const name = idx >= 0 ? part.slice(0, idx) : part;
      const rawValue = idx >= 0 ? part.slice(idx + 1) : '';
      let value = rawValue;
      try { value = decodeURIComponent(rawValue); } catch { /* keep raw */ }
      return { name, value };
    });
};

const getCookieToken = (key) => {
  const name = sessionCookieName(key);
  return parseCookies().find((cookie) => cookie.name === name)?.value || null;
};

const setCookieToken = (key, token) => {
  if (typeof document === 'undefined' || !token) return;
  document.cookie = [
    `${sessionCookieName(key)}=${encodeURIComponent(token)}`,
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
    cookieSecureFlag().trim(),
  ].filter(Boolean).join('; ');
};

const clearCookieToken = (key) => {
  if (typeof document === 'undefined') return;
  const base = `${sessionCookieName(key)}=; Max-Age=0; Path=/; SameSite=Lax${cookieSecureFlag()}`;
  document.cookie = base;
  document.cookie = `${sessionCookieName(key)}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax${cookieSecureFlag()}`;
};

const hasScopedSessionToken = () => storagePair().some((store) => (
  Object.keys(store).some((key) => key.startsWith('session_token:'))
)) || parseCookies().some((cookie) => cookie.name.startsWith(sessionCookieName('session_token:')));

const getLegacySessionToken = () => (
  sessionStorage.getItem(LEGACY_SESSION_TOKEN_KEY)
  || localStorage.getItem(LEGACY_SESSION_TOKEN_KEY)
  || getCookieToken(LEGACY_SESSION_TOKEN_KEY)
);

const isStaffChatEntry = () => {
  if (typeof window === 'undefined') return false;
  const path = String(window.location.pathname || '');
  if (!/(^|\/)admin\/auth\/?$/.test(path)) return false;
  const params = new URLSearchParams(window.location.search || '');
  return params.get('staff_chat') === '1';
};

export const getStoredSessionToken = ({ tenant, portal, allowLegacyFallback = false } = {}) => {
  if (typeof window === 'undefined') return null;
  const scopedKey = sessionScopeKey(tenant, portal);
  const scopedToken = sessionStorage.getItem(scopedKey) || localStorage.getItem(scopedKey) || getCookieToken(scopedKey);
  if (scopedToken) return scopedToken;
  const currentTenant = normalizeKeyPart(tenant || tenantSlugFromPathOrHost());
  const canBootstrapCustomDomain = currentTenant.includes('.');
  if (
    hasScopedSessionToken()
    && !allowLegacyFallback
    && !isStaffChatEntry()
    && !canBootstrapCustomDomain
  ) return null;
  return getLegacySessionToken();
};

export const storeSessionToken = (token, { tenant, portal, sessionOnly = false } = {}) => {
  if (typeof window === 'undefined' || !token) return;
  const scopedKey = sessionScopeKey(tenant, portal);
  const target = sessionOnly ? sessionStorage : localStorage;
  target.setItem(scopedKey, token);
  target.setItem(LEGACY_SESSION_TOKEN_KEY, token);
  if (!sessionOnly) {
    setCookieToken(scopedKey, token);
    setCookieToken(LEGACY_SESSION_TOKEN_KEY, token);
  }
};

export const clearStoredSessionToken = ({ tenant, portal, token } = {}) => {
  if (typeof window === 'undefined') return;
  const scopedKey = sessionScopeKey(tenant, portal);
  for (const store of storagePair()) {
    if (token) {
      Object.keys(store)
        .filter((key) => key.startsWith('session_token:') && store.getItem(key) === token)
        .forEach((key) => store.removeItem(key));
    } else {
      store.removeItem(scopedKey);
    }

    const legacyToken = store.getItem(LEGACY_SESSION_TOKEN_KEY);
    if (!token || legacyToken === token) store.removeItem(LEGACY_SESSION_TOKEN_KEY);
  }
  if (token) {
    parseCookies()
      .filter((cookie) => cookie.name.startsWith(COOKIE_PREFIX))
      .forEach((cookie) => {
        if (
          cookie.value === token
          && (
            cookie.name === sessionCookieName(LEGACY_SESSION_TOKEN_KEY)
            || cookie.name.startsWith(sessionCookieName('session_token:'))
          )
        ) {
          const key = decodeURIComponent(cookie.name.slice(COOKIE_PREFIX.length));
          clearCookieToken(key);
        }
      });
  } else {
    clearCookieToken(scopedKey);
    clearCookieToken(LEGACY_SESSION_TOKEN_KEY);
  }
};

export const clearAllSessionTokens = () => {
  if (typeof window === 'undefined') return;
  for (const store of storagePair()) {
    Object.keys(store)
      .filter((key) => key === LEGACY_SESSION_TOKEN_KEY || key.startsWith('session_token:'))
      .forEach((key) => store.removeItem(key));
  }
  parseCookies()
    .filter((cookie) => (
      cookie.name === sessionCookieName(LEGACY_SESSION_TOKEN_KEY)
      || cookie.name.startsWith(sessionCookieName('session_token:'))
    ))
    .forEach((cookie) => {
      const key = decodeURIComponent(cookie.name.slice(COOKIE_PREFIX.length));
      clearCookieToken(key);
    });
};
